import { useTranslation } from 'react-i18next'
import { ACTIVE_POWER_FUNCTION_MODEL, FulcrumLimits } from '../../types/common'
import { ActivePowerSettingsModel } from '../../types/grid-and-system-protection.model'
import { ActivePowerSettingsForm } from '../../types/schemas'
import { isFieldDisabled } from '../../utils'

export const getDefaultValues = (data: ActivePowerSettingsModel): ActivePowerSettingsForm => {
  return {
    selectedFunction: data.selectedFunction || ACTIVE_POWER_FUNCTION_MODEL.disabled,
    threeTau: data.threeTau.value,
    fulcrums: {
      [ACTIVE_POWER_FUNCTION_MODEL.disabled]: [],
      [ACTIVE_POWER_FUNCTION_MODEL.typeA]:
        data.fulcrums.find(f => ACTIVE_POWER_FUNCTION_MODEL.typeA in f)?.[ACTIVE_POWER_FUNCTION_MODEL.typeA].values || [],
      [ACTIVE_POWER_FUNCTION_MODEL.typeB]:
        data.fulcrums.find(f => ACTIVE_POWER_FUNCTION_MODEL.typeB in f)?.[ACTIVE_POWER_FUNCTION_MODEL.typeB].values || [],
    },
  }
}

export const getFirstFieldDisabledState = (limits: FulcrumLimits[], fulcrumValue: number | null, index: number): boolean => {
  return isFieldDisabled({ value: fulcrumValue, min: limits[index].x.min, max: limits[index].x.max })
}

export const getSecondFieldDisabledState = (limits: FulcrumLimits[], fulcrumValue: number | null, index: number): boolean => {
  return isFieldDisabled({ value: fulcrumValue, min: limits[index].y.min, max: limits[index].y.max })
}

interface AvailableFunctionOption {
  value: ACTIVE_POWER_FUNCTION_MODEL
  label: string
}

export const useAvailableFunctionOptions = (fulcrums: ActivePowerSettingsModel['fulcrums']): AvailableFunctionOption[] => {
  const { t } = useTranslation()

  const values = getAvailableFunctions(fulcrums)

  return [
    {
      value: ACTIVE_POWER_FUNCTION_MODEL.disabled,
      label: t('common.disabled'),
    },
    ...values.map(value => ({
      value,
      label: `[${value}]`,
    })),
  ]
}

const getAvailableFunctions = (fulcrums: ActivePowerSettingsModel['fulcrums']): ACTIVE_POWER_FUNCTION_MODEL[] => {
  return Object.values(ACTIVE_POWER_FUNCTION_MODEL).filter(value => fulcrums.some(v => Object.keys(v)[0] === value))
}

export const getFulcrumFieldHasChangedValues = (
  selectedFunction: ACTIVE_POWER_FUNCTION_MODEL,
  fulcrumItem: ActivePowerSettingsModel['fulcrums'][number],
  index: number
): { firstFieldHasChangedValue: boolean; secondFieldHasChangedValue: boolean } => {
  let firstFieldHasChangedValue = false
  let secondFieldHasChangedValue = false

  if (selectedFunction === ACTIVE_POWER_FUNCTION_MODEL.typeA && ACTIVE_POWER_FUNCTION_MODEL.typeA in fulcrumItem) {
    firstFieldHasChangedValue = fulcrumItem[ACTIVE_POWER_FUNCTION_MODEL.typeA].limits[index].x.changed
    secondFieldHasChangedValue = fulcrumItem[ACTIVE_POWER_FUNCTION_MODEL.typeA].limits[index].y.changed
  } else if (selectedFunction === ACTIVE_POWER_FUNCTION_MODEL.typeB && ACTIVE_POWER_FUNCTION_MODEL.typeB in fulcrumItem) {
    firstFieldHasChangedValue = fulcrumItem[ACTIVE_POWER_FUNCTION_MODEL.typeB].limits[index].x.changed
    secondFieldHasChangedValue = fulcrumItem[ACTIVE_POWER_FUNCTION_MODEL.typeB].limits[index].y.changed
  }

  return { firstFieldHasChangedValue, secondFieldHasChangedValue }
}
